import _ from 'underscore'

import moment from 'moment-timezone'

import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

import Table from 'components/Table'
import Spinner from 'components/Spinner'

import EventNameColumnHeader from 'pages/DeviceAggregator/FaceplateMessages/EventNameColumnHeader'

import useDeviceDetails from 'hooks/useDeviceDetails'
import useFaceplateMessages from 'hooks/useFaceplateMessages'

import { longDateTime } from 'helpers/formatters'
import { DeviceTypes } from 'helpers/constants'

type Props = {
  startDate: string
  endDate: string
  deviceId: string
  timeZone: string
  deviceType: string
}

const noDataMessage = (deviceType: string) => ({
  title: 'No Events',
  description: `${deviceType === DeviceTypes.HEHM ? 'No Messages Received' : 'No Faceplate Messages Received'}`
})

const Events = ({ startDate, endDate, deviceId, timeZone, deviceType }: Props) => {
  const { faceplateMessages, areFaceplateMessagesLoaded } = useFaceplateMessages(deviceId, startDate, endDate)
  const { deviceMetadataDetails, areDeviceDetailsLoaded } = useDeviceDetails(deviceId)
  const [filterEvents, setFilterEvents] = useState<Array<string>>([])

  const uniqueEventNames = (events) => Object.keys(_.groupBy(events || [], event => event.fullyQualifiedName)).sort()

  let deviceTimeZone =
    (areDeviceDetailsLoaded
      ? deviceMetadataDetails?.find((item) => item.column === 'time_zone_name')?.value?.toString()
      : timeZone) ?? timeZone
  if (!deviceTimeZone) deviceTimeZone = 'UTC'

  useEffect(() => {
    if (areFaceplateMessagesLoaded) {
      setFilterEvents(uniqueEventNames(faceplateMessages))
    }
  }, [deviceId, areFaceplateMessagesLoaded, faceplateMessages])

  if (!areFaceplateMessagesLoaded) {
    return <Spinner variant="page" />
  }

  const messageToDisplay = `${deviceType === DeviceTypes.HEHM ? '' : 'Faceplate'} Messages from ${longDateTime(startDate)} 
                              to ${longDateTime(moment(endDate).endOf('days').format())}`

  return (
    <div>
      <span>{messageToDisplay}</span>
      <Table items={faceplateMessages || []} {...{ noDataMessage: noDataMessage(deviceType) }}>
        <Table.Header>
          <Table.HeaderCell title="Time" />
          <EventNameColumnHeader
            filterOptions={uniqueEventNames(faceplateMessages)}
            selectedFilterOptions={filterEvents}
            onChangeFilterOptionsCallback={setFilterEvents}
          />
          <S.PayloadHeaderCell title="Payload" />
        </Table.Header>
        {_.filter(faceplateMessages || [], event => filterEvents.includes(event.fullyQualifiedName))
          .map((faceplateMessage) => (
            <Table.Row key={faceplateMessage.guid}>
              <S.Cell>
                {moment.unix(faceplateMessage.occurredAt).tz(deviceTimeZone).format()}
              </S.Cell>
              <S.Cell>{faceplateMessage.fullyQualifiedName}</S.Cell>
              <S.PayloadCell>
                <S.PayloadContent>{JSON.stringify(faceplateMessage, null, 2)}</S.PayloadContent>
              </S.PayloadCell>
            </Table.Row>
          ))}
      </Table>
    </div>
  )
}

export default Events

const S = {
  Cell: styled(Table.Cell)`
    font-weight: bold;
    font-size: 14px;
    flex: 1;
    padding: 20px 0;
  `,
  PayloadHeaderCell: styled(Table.HeaderCell)`
    flex: 2;
  `,
  PayloadCell: styled(Table.Cell)`
    flex: 2;
    font-size: 14px;
    padding: 20px 0;
  `,
  PayloadContent: styled.pre`
    white-space: pre-wrap;
    overflow-wrap: break-word;
    max-width: 100%;
  `
}
